import { IndicatorType } from '@shared/types';
import { useEffect } from 'react';
import { IndicatorUpdate } from '../services/indicator-update.service';
import { useChartStreamContext } from '../components/chart-stream.context';

type UseIndicatorUpdateStreamParams<TUpdate extends IndicatorUpdate> = {
  chartInitialized: boolean;
  enabled: boolean;
  indicatorType: IndicatorType;
  isExpectedUpdate: (update: IndicatorUpdate) => update is TUpdate;
  applyUpdate: (update: TUpdate) => void;
};

export const useIndicatorUpdateStream = <TUpdate extends IndicatorUpdate>({
  chartInitialized,
  enabled,
  indicatorType,
  isExpectedUpdate,
  applyUpdate
}: UseIndicatorUpdateStreamParams<TUpdate>) => {
  const { indicatorUpdate$, getIndicatorSnapshots } = useChartStreamContext();

  useEffect(() => {
    if (!chartInitialized || !enabled) return;

    // Replay latest snapshot before listening for incremental updates.
    getIndicatorSnapshots(indicatorType)
      .filter(isExpectedUpdate)
      .forEach(applyUpdate);

    const subscription = indicatorUpdate$.subscribe((update) => {
      if (!isExpectedUpdate(update)) return;

      applyUpdate(update);
    });

    return () => subscription.unsubscribe();
  }, [
    applyUpdate,
    chartInitialized,
    enabled,
    getIndicatorSnapshots,
    indicatorUpdate$,
    indicatorType,
    isExpectedUpdate
  ]);
};
